
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import { ensureIdentity } from './lib/identity';

// Apply persisted CRT / reduced-motion settings before first paint
try {
  const raw = localStorage.getItem('monolith.settings');
  const s = raw ? JSON.parse(raw) : {};
  document.documentElement.dataset.crt = s.crtIntensity || 'medium';
  document.documentElement.dataset.reducedMotion = s.reducedMotion ? '1' : '0';
} catch {
  document.documentElement.dataset.crt = 'medium';
}

// Fire-and-forget anonymous bootstrap so guests have a backend identity.
ensureIdentity();
import { 
  registerServiceWorker, 
  setupServiceWorkerAuthListener, 
  setupStandaloneModeListener 
} from './utils/pwa';
import { logger } from './utils/logger';
import { safeReload } from './utils/navigation';

// Initialize performance monitoring
if (import.meta.env.DEV) {
  console.time('App bootstrap');
}

// Initialize logging
logger.info('Application bootstrap starting');

// Lightweight service worker registration - don't block app startup
Promise.resolve().then(async () => {
  try {
    const success = await registerServiceWorker();
    logger.info('Service worker registration', { success });
    
    // Set up PWA features only after successful registration
    setupStandaloneModeListener();
    setupServiceWorkerAuthListener();
  } catch (error) {
    logger.error('Service worker registration failed', error);
  }
});

// Conditionally load dev tools and optimizers
if (import.meta.env.DEV) {
  import('./utils/dev/PerformanceDevTools').then(({ devPerformanceTools }) => {
    devPerformanceTools.initializeDevTools();
  });
} else {
  // Load bundle optimizer in production
  import('./utils/lazy-imports').then(({ preloadCriticalChunks }) => {
    preloadCriticalChunks();
  });
}

// Initialize the app
const root = document.getElementById('root');

if (!root) {
  const error = new Error('Root element not found');
  logger.error('Critical error: Root element not found', error);
  throw error;
} else {
  try {
    logger.info('Initializing React application');
    
    ReactDOM.createRoot(root).render(
      <App />
    );
    
if (import.meta.env.DEV) {
  console.timeEnd('App bootstrap');
  logger.debug('App bootstrap completed');
}
  } catch (error) {
    logger.error('Failed to render app', error);
    
    // Fallback rendering in case of critical error
    ReactDOM.createRoot(root).render(
      <div className="p-4 max-w-md mx-auto mt-8 bg-red-50 border border-red-200 rounded">
        <h1 className="text-xl font-bold text-red-700 mb-2">Application Error</h1>
        <p className="text-red-600 mb-4">
          We encountered an error loading the application. Please try refreshing the page.
        </p>
        <button 
          onClick={() => safeReload('bootstrap-error')}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Refresh
        </button>
      </div>
    );
  }
}
